import {
  NoToneMapping,
  PCFSoftShadowMap,
  PerspectiveCamera,
  Scene,
  Vector3,
  WebGLRenderer,
  type MeshStandardMaterial,
  type Vector3Tuple,
  MathUtils,
} from 'three';
import { CSM } from 'three/examples/jsm/csm/CSM.js';
import {
  DEFAULT_CHARACTER,
  type CharacterConfig,
} from '../character/CharacterDefinition';
import { CharacterAnimator } from '../character/CharacterAnimator';
import { CharacterLoader, type CharacterInstance } from '../character/CharacterModel';
import { FishingRod } from '../character/FishingRod';
import { FishingSystem } from '../fishing/FishingSystem';
import { PlayerHealth } from '../combat/PlayerHealth';
import { SpiderManager, type SpiderTarget } from '../combat/SpiderManager';
import { Weapon } from '../combat/Weapon';
import { HAND_R_SOCKET, WEAPON_GRIPS, loadAttachmentOverrides } from '../combat/attachments';
import { ThirdPersonControls } from '../character/ThirdPersonControls';
import { ADAPTIVE, GRASS, NET, RENDER, SUN, WORLD } from '../config';
import { Clouds } from '../render/Clouds';
import { Environment } from '../render/Environment';
import { PostFX } from '../render/PostFX';
import { CharacterMenu } from '../ui/CharacterMenu';
import { DeathScreen } from '../ui/DeathScreen';
import { AudioManager } from '../audio/AudioManager';
import { Hud } from '../ui/Hud';
import { ChatPanel } from '../ui/ChatPanel';
import { FitPanel } from '../ui/FitPanel';
import { NetworkClient, type ConnectionStatus } from '../net/NetworkClient';
import { RemotePlayers } from '../net/RemotePlayers';
import { animToCode, PLAYER_MAX_HP, SWORD_DAMAGE, type NetAppearance } from '../net/protocol';
import { Grass } from '../world/Grass';
import { HeightField } from '../world/HeightField';
import { Terrain } from '../world/Terrain';
import type { TerrainTextureSet } from '../world/TerrainTextures';
import { Water } from '../world/Water';
import { Splashes } from '../world/Splashes';
import { FlyCamera } from './FlyCamera';

export type EngineMode = 'customizer' | 'playing';

export class Engine {
  readonly renderer: WebGLRenderer;
  readonly scene = new Scene();
  readonly camera: PerspectiveCamera;
  readonly terrain: Terrain;
  readonly water: Water;
  readonly splashes: Splashes;
  readonly environment: Environment;
  readonly clouds: Clouds;
  readonly grass: Grass | null = null;
  readonly heightField: HeightField;
  readonly audio = new AudioManager();

  readonly characterLoader = new CharacterLoader();
  character: CharacterInstance | null = null;
  characterAnimator: CharacterAnimator | null = null;
  playerControls: ThirdPersonControls | null = null;
  fishingRod: FishingRod | null = null;
  fishing: FishingSystem | null = null;
  spiders: SpiderManager | null = null;
  weapon: Weapon | null = null;
  health: PlayerHealth | null = null;
  deathScreen: DeathScreen | null = null;
  /** Životy ostatních hráčů — pavouci nemají útočit na mrtvé. */
  private readonly remoteHp = new Map<string, number>();
  private readonly spiderTargets: SpiderTarget[] = [];
  network: NetworkClient | null = null;
  remotePlayers: RemotePlayers | null = null;
  private netStatusEl: HTMLElement | null = null;
  private chat: ChatPanel | null = null;
  private fitPanel: FitPanel | null = null;
  private inspectYaw = 0;

  /**
   * Prohlížecí režim pro ladění posazení předmětů.
   *
   * Zmrazí ovládání a postaví kameru na pevné místo u postavy. Bez toho
   * herní smyčka každý snímek přepisuje natočení i kameru a postava se
   * s posuvníky pere.
   */
  private setInspectMode(active: boolean): void {
    if (!this.playerControls || !this.character) return;
    this.playerControls.paused = active;

    if (!active) return;
    const base = this.character.root.position;
    this.character.root.rotation.y = this.inspectYaw;
    // Zblízka a z boku — tam je posazení zbraně v ruce nejlíp vidět.
    this.camera.position.set(base.x + 1.6, base.y + 1.15, base.z + 1.3);
    this.camera.lookAt(base.x, base.y + 0.95, base.z);
    this.camera.updateMatrixWorld();
  }
  private fishingPromptEl: HTMLElement | null = null;
  private fishingPromptTimer = 0;

  /**
   * Hláška uprostřed dole. Nepoužívá chat, protože ta sdělení jsou okamžitá
   * a v historii by jen překážela.
   */
  private healthBarEl: HTMLElement | null = null;

  /** Ukazatel životů vlevo dole. */
  private renderHealthBar(hp: number): void {
    if (!this.healthBarEl) {
      this.healthBarEl = document.createElement('div');
      this.healthBarEl.id = 'player-health';
      this.healthBarEl.innerHTML = '<i></i><span></span>';
      document.body.appendChild(this.healthBarEl);
    }
    const ratio = Math.max(0, hp) / PLAYER_MAX_HP;
    const fill = this.healthBarEl.firstElementChild as HTMLElement;
    const label = this.healthBarEl.lastElementChild as HTMLElement;
    fill.style.width = `${ratio * 100}%`;
    // Barva se mění se stavem — číslo si hráč v zápalu boje nepřečte.
    fill.style.background = ratio > 0.5 ? '#7ee08a' : ratio > 0.25 ? '#ffd479' : '#ff6b6b';
    label.textContent = `${Math.max(0, Math.round(hp))} / ${PLAYER_MAX_HP}`;
  }

  /** Vyhodnotí seknutí mečem v okamžiku, kdy meč prochází obloukem. */
  private resolveSwing(): void {
    if (!this.spiders || !this.character) return;
    const origin = this.character.root.position;
    const target = this.spiders.findTargetInArc(origin, this.character.root.rotation.y, 2.8, Math.PI / 3);
    if (target === null) return;

    // Zásah se pošle i sobě: uplatní ho jen ten, kdo pavouky simuluje,
    // takže se nemůže započítat dvakrát.
    this.network?.sendHit(target, SWORD_DAMAGE);
    this.spiders.damage(target, SWORD_DAMAGE);
  }

  /**
   * Kdo simuluje nepřátele.
   *
   * Bere se hráč s nejmenším id — všichni znají tentýž seznam, takže se
   * na majiteli shodnou bez domlouvání. Offline simuluje každý sám sobě.
   */
  private updateSpiderOwnership(): void {
    if (!this.spiders) return;
    const selfId = this.network?.id;
    if (!selfId) {
      this.spiders.setOwner(true);
      return;
    }
    let lowest = selfId;
    for (const id of this.remoteHp.keys()) {
      if (id < lowest) lowest = id;
    }
    this.spiders.setOwner(lowest === selfId);
  }

  private showFishingPrompt(text: string, color = '#e6ecf4'): void {
    if (!this.fishingPromptEl) {
      this.fishingPromptEl = document.createElement('div');
      this.fishingPromptEl.id = 'fishing-prompt';
      document.body.appendChild(this.fishingPromptEl);
    }
    this.fishingPromptEl.textContent = text;
    this.fishingPromptEl.style.color = color;
    this.fishingPromptEl.classList.add('visible');

    window.clearTimeout(this.fishingPromptTimer);
    this.fishingPromptTimer = window.setTimeout(() => {
      this.fishingPromptEl?.classList.remove('visible');
    }, 3200);
  }
  flyControls: FlyCamera | null = null;
  characterMenu: CharacterMenu | null = null;

  mode: EngineMode = 'customizer';

  private readonly postFX: PostFX;
  private readonly csm: CSM;
  private readonly hud = new Hud();
  private readonly container: HTMLElement;

  private renderScale: number = ADAPTIVE.maxScale;
  private grassQuality = 1;
  private basePixelRatio: number;
  private adaptiveTimer = 0;
  private adaptiveFrames = 0;
  private adaptiveElapsed = 0;
  private lastTime = 0;
  private elapsed = 0;
  private running = false;
  private frameHandle = 0;

  // Customizer preview rotation
  private previewRotation = 0;
  /** Náklon náhledové kamery: + se dívá shora, − zdola. */
  private previewPitch = 0;
  /** Vzdálenost kamery od postavy. */
  private previewDistance = 2.5;
  /** Výška bodu, kolem kterého kamera obíhá (0 = u nohou). */
  private previewHeight = 1.05;
  private lastMouseY = 0;
  /** Pomalé otáčení náhledu. Výchozí je vypnuté — stojící postava se
   *  posuzuje líp než rotující. Zapíná se z tvorby postavy. */
  private previewAutoRotate = false;
  private isDragging = false;
  private lastMouseX = 0;
  private previewSpawnY = 0;

  constructor(
    container: HTMLElement,
    heightField: HeightField,
    textures: TerrainTextureSet,
  ) {
    this.container = container;
    this.heightField = heightField;

    this.renderer = new WebGLRenderer({
      antialias: false,
      powerPreference: 'high-performance',
      stencil: false,
      depth: true,
    });
    this.basePixelRatio = Math.min(window.devicePixelRatio || 1, RENDER.maxPixelRatio);
    this.renderer.setPixelRatio(1);
    this.renderer.toneMapping = NoToneMapping;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = PCFSoftShadowMap;
    this.renderer.shadowMap.autoUpdate = true;
    this.renderer.info.autoReset = false;
    container.appendChild(this.renderer.domElement);

    this.camera = new PerspectiveCamera(RENDER.fov, 1, RENDER.near, RENDER.far);

    this.environment = new Environment(this.scene, this.renderer);

    this.csm = new CSM({
      camera: this.camera,
      parent: this.scene,
      cascades: RENDER.shadowCascades,
      maxFar: RENDER.shadowMaxFar,
      mode: 'practical',
      shadowMapSize: RENDER.shadowMapSize,
      shadowBias: -0.00018,
      lightDirection: this.environment.sunDirection.clone(),
      lightIntensity: SUN.intensity,
      lightMargin: 240,
    });
    this.csm.fade = true;

    const setupShadows = (material: MeshStandardMaterial) => this.csm.setupMaterial(material);

    this.terrain = new Terrain(heightField, textures, setupShadows);
    this.scene.add(this.terrain.group);

    this.water = new Water(heightField.texture, setupShadows);
    this.splashes = new Splashes(this.scene, WORLD.seaLevel);
    this.scene.add(this.water.mesh);

    this.clouds = new Clouds();
    this.scene.add(this.clouds.mesh);

    if (GRASS.enabled) {
      this.grass = new Grass(heightField.texture, textures.grassColor, setupShadows);
      this.scene.add(this.grass.mesh);
    }

    this.postFX = new PostFX(this.renderer, this.scene, this.camera);

    window.addEventListener('resize', this.onResize);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.setupCustomizerDrag();
    this.onResize();
  }

  async initCharacter(config: CharacterConfig = DEFAULT_CHARACTER): Promise<void> {
    if (this.character) {
      this.scene.remove(this.character.root);
      this.character.dispose();
      this.characterAnimator?.dispose();
    }

    this.character = await this.characterLoader.createCharacter(config);
    this.characterAnimator = new CharacterAnimator(this.character);
    this.scene.add(this.character.root);

    // Place at scenic terrain location
    const spawnX = 0;
    const spawnZ = 0;
    const groundY = this.heightField.heightAt(spawnX, spawnZ);
    this.previewSpawnY = Math.max(WORLD.seaLevel + 1.0, groundY);
    this.character.root.position.set(spawnX, this.previewSpawnY, spawnZ);

    this.setupCustomizerCamera();

    if (!this.characterMenu) {
      this.characterMenu = new CharacterMenu(
        document.body,
        config,
        {
          onConfigChange: async (newConfig, changedProp) => {
            if (changedProp === 'gender') {
              await this.initCharacter(newConfig);
            } else if (this.character) {
              if (changedProp === 'hairstyle') {
                await this.character.setHairstyle(newConfig.hairstyle);
              } else if (changedProp === 'beard') {
                await this.character.setBeard(newConfig.beard);
              } else if (changedProp === 'eyebrows') {
                await this.character.setEyebrows(newConfig.eyebrows);
              } else if (changedProp === 'skinTone') {
                await this.character.setSkinTone(newConfig.skinTone);
              } else if (changedProp === 'hairColor') {
                this.character.setHairColor(newConfig.hairColor);
              } else if (changedProp === 'outfit') {
                await this.character.setOutfit(newConfig.outfit, newConfig.hood);
              } else if (changedProp === 'hood') {
                this.character.setHood(newConfig.hood);
              }
            }
          },
          onEnterWorld: () => {
            this.enterWorld();
          },
          onRotatePreview: (delta) => {
            this.previewRotation += delta;
            if (this.character) this.character.root.rotation.y = this.previewRotation;
          },
          onAutoRotate: (enabled) => {
            this.previewAutoRotate = enabled;
          },
        },
      );
    }
  }

  /**
   * Připojí se k herní zóně.
   *
   * Volá se až při vstupu do světa, ne při startu — v tvorbě postavy se
   * nikam nepřipojuje, takže se Durable Object nebudí zbytečně a hráč,
   * který si jen prohlíží účesy, neblokuje místo v zóně.
   */
  private connectToServer(): void {
    if (!NET.enabled || !NET.serverUrl || this.network) return;
    if (!this.character) return;

    this.remotePlayers = new RemotePlayers(this.scene, this.characterLoader, document.body);
    // Ostatní hráči šplouchají taky. Nic se kvůli tomu neposílá navíc —
    // efekt se odvodí z pozice, která po síti chodí tak jako tak.
    this.remotePlayers.waterProbe = (x, z) => ({
      seaLevel: WORLD.seaLevel,
      groundY: this.heightField.heightAt(x, z),
    });
    this.remotePlayers.onSplash = (x, z, strength) => {
      this.splashes.splash(x, z, strength);
      // Cizí šplouchnutí je tlumené — jinak by při davu ve vodě zněla kaše.
      this.audio.playSplash(strength * 0.5);
    };
    this.remotePlayers.onWade = (x, z, dirX, dirZ, strength) => {
      this.splashes.wade(x, z, dirX, dirZ, strength);
    };
    this.chat = new ChatPanel(document.body, (text) => this.network?.sendChat(text));
    this.netStatusEl = document.createElement('div');
    this.netStatusEl.id = 'net-status';
    document.body.appendChild(this.netStatusEl);

    const url = `${NET.serverUrl}?zone=${encodeURIComponent(NET.zone)}`;
    this.network = new NetworkClient(url, {
      onStatus: (status, detail) => this.renderNetStatus(status, detail),
      onWelcome: (_id, players, _resume) => {
        // Návrat na uloženou pozici je zatím vypnutý: všichni začínají na
        // stejném místě, aby se hráči po připojení našli. Server si pozici
        // dál ukládá, takže se to dá kdykoliv zapnout zpátky.
        // Roster přijde kompletní i po znovupřipojení, takže se staré
        // instance zahodí — jinak by po výpadku sítě zůstali ve světě
        // duchové hráčů, kteří mezitím odešli.
        this.remotePlayers?.clear();
        this.fishing?.fishSchool.clear();
        this.remoteHp.clear();
        for (const player of players) {
          this.remotePlayers?.add(player);
          this.remoteHp.set(player.id, player.hp ?? PLAYER_MAX_HP);
          // Kdo rybařil ještě před naším příchodem, musí mít hejno hned.
          if (player.spot) {
            this.fishing?.fishSchool.setSpot(
              player.id,
              new Vector3(player.spot[0], WORLD.seaLevel, player.spot[1]),
            );
          }
        }
        this.updateSpiderOwnership();
        this.renderNetStatus('online');
      },
      onSpawn: (player) => {
        this.remotePlayers?.add(player);
        this.remoteHp.set(player.id, player.hp ?? PLAYER_MAX_HP);
        this.updateSpiderOwnership();
        this.renderNetStatus('online');
        if (player.spot) {
          this.fishing?.fishSchool.setSpot(
            player.id,
            new Vector3(player.spot[0], WORLD.seaLevel, player.spot[1]),
          );
        }
        this.chat?.addSystem(`${player.name} se připojil.`);
      },
      onLeave: (id) => {
        this.remotePlayers?.remove(id);
        // Bez tohohle by po odchodu rybáře jeho hejno skákalo dál navždy.
        this.fishing?.fishSchool.removeSpot(id);
        this.remoteHp.delete(id);
        // Odchod majitele znamená, že simulaci nepřátel někdo musí převzít.
        this.updateSpiderOwnership();
        this.renderNetStatus('online');
      },
      onState: (id, p, r, a, at) => this.remotePlayers?.pushState(id, p, r, a, at),
      onLook: (id, appearance) => this.remotePlayers?.setAppearance(id, appearance),
      onChat: (id, name, message) => this.chat?.addMessage(name, message, id === this.network?.id),
      onNpcState: (_from, state) => this.spiders?.applyRemoteState(state),
      onHit: (id, dmg) => this.spiders?.damage(id, dmg),
      onPlayerDamage: (id, dmg) => {
        // Zásah si uplatní jen ten, komu patří — životy si spravuje každý sám.
        if (id === this.network?.id) this.health?.damage(dmg);
      },
      onHp: (id, hp) => {
        this.remoteHp.set(id, hp);
        this.updateSpiderOwnership();
      },
      onSpot: (id, p) => {
        // Skákající ryby u cizího splávku počítá každý klient sám z těchto
        // souřadnic a společného času — proto se nic dalšího neposílá.
        const school = this.fishing?.fishSchool;
        if (!school) return;
        if (p) school.setSpot(id, new Vector3(p[0], WORLD.seaLevel, p[1]));
        else school.removeSpot(id);
      },
      onCorrection: (p, reason) => {
        // Server odmítl pozici — postava se vrací tam, kde ji naposledy
        // uznal. Bez toho by se klient a server rozešli natrvalo.
        // Serverová pozice se bere jako platná, ale nikdy ne pod terén —
        // jinak by korekce hráče zavrtaného do země v zemi jen utvrdila.
        const safeY = Math.max(p[1], this.heightField.heightAt(p[0], p[2]));
        this.playerControls?.placeAt(p[0], p[2]);
        this.character?.root.position.set(p[0], safeY, p[2]);
        console.warn(`[net] server odmítl pozici: ${reason}`);
      },
    });

    this.network.connect(this.character.config.name, toNetAppearance(this.character.config));
  }

  private renderNetStatus(status: ConnectionStatus, detail?: string): void {
    if (!this.netStatusEl) return;
    this.netStatusEl.dataset.status = status;
    const others = this.remotePlayers?.count ?? 0;
    const ping = this.network?.ping ?? 0;

    const label =
      status === 'online'
        ? `online · ${others + 1} hráčů${ping ? ` · ${ping} ms` : ''}`
        : status === 'connecting'
          ? 'připojuji…'
          : status === 'error'
            ? `offline · ${detail ?? 'chyba'}`
            : 'offline';
    this.netStatusEl.textContent = label;
  }

  private setupCustomizerCamera(): void {
    if (!this.character) return;
    this.previewRotation = 0;
    this.previewPitch = 0;
    this.previewDistance = 2.5;
    this.previewHeight = 1.05;
    this.character.root.rotation.y = 0;
    this.applyCustomizerCamera();
  }

  /**
   * Obíhající kamera náhledu.
   *
   * Postavu je potřeba si prohlédnout ze všech stran, i shora a zdola a
   * zblízka — jinak se chyba na zátylku nebo pod bradou nedá ani ukázat,
   * natož najít. Vodorovné tažení otáčí postavou, svislé naklání kameru,
   * kolečko přibližuje.
   */
  private applyCustomizerCamera(): void {
    if (!this.character) return;
    const charPos = this.character.root.position;
    const targetX = charPos.x + 0.12;
    const targetY = charPos.y + this.previewHeight;
    const targetZ = charPos.z;

    const cos = Math.cos(this.previewPitch);
    const sin = Math.sin(this.previewPitch);
    // Postava stojí vlevo od menu; posun se krátí s přiblížením, jinak by
    // při detailu vyjela mimo záběr.
    const sideways = -0.45 * (this.previewDistance / 2.5);

    this.camera.position.set(
      targetX + sideways,
      targetY + sin * this.previewDistance,
      targetZ + cos * this.previewDistance,
    );
    this.camera.lookAt(targetX, targetY, targetZ);
  }

  private setupCustomizerDrag(): void {
    const el = this.renderer.domElement;
    el.addEventListener('mousedown', (e) => {
      if (this.mode !== 'customizer') return;
      this.isDragging = true;
      this.lastMouseX = e.clientX;
      this.lastMouseY = e.clientY;
    });

    window.addEventListener('mousemove', (e) => {
      if (!this.isDragging || this.mode !== 'customizer' || !this.character) return;
      const deltaX = e.clientX - this.lastMouseX;
      const deltaY = e.clientY - this.lastMouseY;
      this.lastMouseX = e.clientX;
      this.lastMouseY = e.clientY;

      this.previewRotation += deltaX * 0.008;
      this.character.root.rotation.y = this.previewRotation;

      // Náklon se omezuje těsně pod svislici. Za ní se kamera převrátí
      // vzhůru nohama, protože lookAt ztratí referenci, co je nahoře.
      this.previewPitch = MathUtils.clamp(this.previewPitch + deltaY * 0.006, -1.35, 1.35);
      this.applyCustomizerCamera();
    });

    window.addEventListener('mouseup', () => {
      this.isDragging = false;
    });

    el.addEventListener(
      'wheel',
      (e) => {
        if (this.mode !== 'customizer') return;
        e.preventDefault();
        // Násobek, ne přičítání: krok přiblížení pak sedí stejně
        // z odstupu i v detailu.
        this.previewDistance = MathUtils.clamp(
          this.previewDistance * (1 + Math.sign(e.deltaY) * 0.12),
          0.45,
          6,
        );
        this.applyCustomizerCamera();
      },
      { passive: false },
    );
  }

  /**
   * Vyrenderuje jeden snímek z daného místa a kameru vrátí, kde byla.
   *
   * Slouží vývojovému snímkování (`src/dev/capture.ts`) — grafika se nedá
   * ladit přes čísla. Renderuje se přes composer, protože přímý
   * `renderer.render` přeskočí expozici a tone mapping a výsledek je bílá
   * plocha.
   */
  renderFrom(from: Vector3Tuple, at: Vector3Tuple, advanceSeconds = 0): void {
    // Posun herního času. Animované efekty (vlny, šplouchání) se jinak na
    // snímku nehnou, protože smyčka běží na requestAnimationFrame a ten ve
    // skrytém okně stojí — sekvence by pak byla samý identický snímek.
    this.elapsed += advanceSeconds;
    const saved = {
      position: this.camera.position.clone(),
      quaternion: this.camera.quaternion.clone(),
    };
    this.camera.position.set(...from);
    this.camera.lookAt(...at);
    this.camera.updateMatrixWorld(true);
    // Terén i tráva se cullují a přepínají LOD podle kamery. Bez tohohle
    // kroku zůstanou chunky kolem nového místa odculované z předchozí pozice
    // a snímek ukáže prázdno — což se snadno splete s chybou v renderu.
    this.terrain.update(this.camera);
    this.grass?.update(this.elapsed, this.camera, this.environment.sunDirection);
    // Moře jede s kamerou, po přesunu se musí přepočítat.
    this.water.update(this.elapsed, this.camera, this.environment.sunDirection);
    this.splashes.update(this.elapsed, this.camera);
    this.postFX.render(1 / 60);
    this.camera.position.copy(saved.position);
    this.camera.quaternion.copy(saved.quaternion);
    this.camera.updateMatrixWorld(true);
  }

  enterWorld(): void {
    if (!this.character || !this.characterAnimator) return;
    this.mode = 'playing';

    this.playerControls = new ThirdPersonControls(
      this.character,
      this.characterAnimator,
      this.camera,
      this.renderer.domElement,
      this.heightField,
    );

    // Šplouchání: efekt na hladině a k němu procedurální zvuk.
    this.playerControls.onWaterSplash = (x, z, strength) => {
      this.splashes.splash(x, z, strength);
      this.audio.playSplash(strength);
    };
    this.playerControls.onWaterWade = (x, z, dirX, dirZ, strength) => {
      this.splashes.wade(x, z, dirX, dirZ, strength);
    };

    this.playerControls.placeAt(0, 0);

    // Prut visí na kosti ruky, takže ho stačí připnout jednou — dál ho
    // animace vozí sama.
    this.fishingRod = new FishingRod(this.characterLoader);
    if (this.fishingRod.attach(this.character)) {
      void this.fishingRod.setLevel(1);
      this.playerControls.rod = this.fishingRod;
    }

    // Doladěné posazení z minula se musí načíst dřív, než zbraň vznikne.
    loadAttachmentOverrides();
    this.weapon = new Weapon(this.characterLoader);
    if (this.weapon.attach(this.character)) {
      void this.weapon.setModel('Sword_Golden');
      this.weapon.show(true);
      this.playerControls.weapon = this.weapon;

      // Ladicí panel pro posazení meče v ruce. Skrytý pod F9 — je to
      // vývojářský nástroj, ne herní funkce.
      const weapon = this.weapon;
      this.fitPanel = new FitPanel(document.body, {
        title: 'Posazení meče',
        fields: [
          { key: 'rx', section: 'Socket ruky', hint: 'platí pro všechny zbraně', label: 'otoč X', min: -3.15, max: 3.15, step: 0.01, fine: 0.005, value: HAND_R_SOCKET.rotation.x },
          { key: 'ry', label: 'otoč Y', min: -3.15, max: 3.15, step: 0.01, fine: 0.005, value: HAND_R_SOCKET.rotation.y },
          { key: 'rz', label: 'otoč Z', min: -3.15, max: 3.15, step: 0.01, fine: 0.005, value: HAND_R_SOCKET.rotation.z },
          { key: 'x', label: 'posun X', min: -0.3, max: 0.3, step: 0.002, fine: 0.001, value: HAND_R_SOCKET.offset.x },
          { key: 'y', label: 'posun Y', min: -0.3, max: 0.3, step: 0.002, fine: 0.001, value: HAND_R_SOCKET.offset.y },
          { key: 'z', label: 'posun Z', min: -0.3, max: 0.3, step: 0.002, fine: 0.001, value: HAND_R_SOCKET.offset.z },
          { key: 'grip', section: 'Grip zbraně', hint: 'jen tenhle model', label: 'jílec', min: -1, max: 5.5, step: 0.01, fine: 0.005, value: WEAPON_GRIPS.Sword_Golden.gripOffset },
          { key: 'scale', label: 'velikost', min: 0.02, max: 0.5, step: 0.002, fine: 0.001, value: WEAPON_GRIPS.Sword_Golden.scale },
          { key: 'otoc', section: 'Náhled', hint: 'nic neukládá, jen otáčí', label: 'postava', min: -3.15, max: 3.15, step: 0.02, fine: 0.02, value: 0 },
        ],
        apply: (values) => {
          // Otáčení postavy není součást posazení meče, jen pomůcka —
          // bez ní se dá zbraň prohlédnout jen z jedné strany.
          this.inspectYaw = values.otoc ?? 0;
          if (this.character) this.character.root.rotation.y = this.inspectYaw;
          return weapon.tune(values);
        },
        onToggle: (open) => this.setInspectMode(open),
      });
    }

    this.deathScreen = new DeathScreen(document.body);

    this.health = new PlayerHealth({
      onChange: (hp) => {
        this.renderHealthBar(hp);
        this.network?.sendHp(hp);
      },
      onDeath: () => {
        this.characterAnimator?.playDeath();
        this.deathScreen?.show(() => {
          this.health?.respawn();
        }, 3);
      },
      onRespawn: () => {
        // Návrat na výchozí bod. Kdyby se hráč vrátil tam, kde padl,
        // stál by rovnou v pavoukovi a zemřel znovu.
        this.deathScreen?.hide();
        this.playerControls?.placeAt(0, 0);
        this.characterAnimator?.setMovementState(false, false, false);
        this.showFishingPrompt('Jsi zpátky ve hře.', '#7ee08a');
      },
    });
    this.playerControls.health = this.health;
    this.renderHealthBar(PLAYER_MAX_HP);

    this.spiders = new SpiderManager(
      this.scene,
      this.characterLoader,
      this.heightField,
      document.body,
      {
        onBite: (targetId, damage) => {
          // Simulující klient jen oznámí zásah; životy si spravuje každý sám.
          if (targetId === 'self') this.health?.damage(damage);
          else this.network?.sendPlayerDamage(targetId, damage);
        },
        onBroadcast: (state) => this.network?.sendNpcState(state),
      },
    );
    void this.spiders.preload();
    // Vlastnictví se určuje i tady, ne jen při příchodu hráčů: uvítací
    // zpráva ze serveru může dorazit dřív, než pavouci vůbec vzniknou,
    // a pak by se nastavení zahodilo a nikdo by je nesimuloval.
    this.updateSpiderOwnership();

    // Seknutí vyhodnotí zásah uprostřed klipu, ne při stisku tlačítka.
    this.playerControls.onSwing = () => this.resolveSwing();

    this.fishing = new FishingSystem(this.scene, this.characterLoader, this.heightField, {
      onPrompt: (text, color) => this.showFishingPrompt(text, color),
      onSpotChange: (p) => this.network?.sendSpot(p),
      onCatch: (fish) => {
        // Přes síť, ne jen do vlastního chatu — server rozesílá zprávu
        // i zpátky odesílateli, takže se úlovek objeví oběma naráz
        // a nemusí se řešit, aby se lokálně nevypsal dvakrát.
        if (this.network?.connectionStatus === 'online') {
          this.network.sendChat(`ulovil ${fish.name}`);
        } else {
          this.chat?.addSystem(`Ulovil jsi: ${fish.name}`);
        }
      },
    });
    this.playerControls.fishingSystem = this.fishing;
    void this.fishing.preload();

    this.connectToServer();

    const help = document.getElementById('help');
    if (help) {
      help.textContent =
        'WASD pohyb · Shift běh · Mezerník skok · Levé tlačítko sekni · F rybaření · X tanec · Enter chat · F9 posazení meče';
    }

    const clickHint = document.getElementById('click');
    if (clickHint) {
      clickHint.textContent = 'Klikni pro ovládání postavy';
      clickHint.classList.remove('hidden');
    }
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.frameHandle = requestAnimationFrame(this.tick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.frameHandle);
  }

  async warmUp(): Promise<void> {
    this.terrain.update(this.camera);
    await this.renderer.compileAsync(this.scene, this.camera);
  }

  private tick = (now: number): void => {
    if (!this.running) return;
    this.frameHandle = requestAnimationFrame(this.tick);

    const dt = Math.min((now - this.lastTime) / 1000, 0.1);
    this.lastTime = now;
    this.elapsed += dt;

    this.renderer.info.reset();
    const cpuStart = performance.now();

    // Vlny, mraky, vítr i skákající ryby se počítají z času. Když jsme
    // připojení, bere se čas ze serveru — jinak by každý hráč viděl jinou
    // vodu, jiné mraky a jiné hejno, protože lokální `elapsed` startuje
    // na nule při načtení stránky.
    const effectTime =
      this.network?.hasWorldClock === true ? this.network.worldTime() : this.elapsed;

    if (this.mode === 'customizer') {
      this.characterAnimator?.update(dt);
      if (this.previewAutoRotate && !this.isDragging && this.character) {
        this.previewRotation += dt * 0.35;
        this.character.root.rotation.y = this.previewRotation;
      }
      // Postava se v náhledu může posunout (dopadne na terén), kamera na ni
      // proto musí držet každý snímek, ne jen při vstupu do menu.
      this.applyCustomizerCamera();
    } else if (this.mode === 'playing') {
      // Rybaření potřebuje společný čas — skoky ryb se z něj počítají
      // deterministicky, takže je všichni hráči vidí stejně.
      this.playerControls?.update(dt, effectTime);

      if (this.network && this.character && this.characterAnimator) {
        const p = this.character.root.position;
        // Klient si sám hlídá takt i to, jestli se vůbec něco změnilo.
        this.network.sendState(
          p.x,
          p.y,
          p.z,
          this.character.root.rotation.y,
          animToCode(this.characterAnimator.state),
        );
      }
      this.remotePlayers?.update(dt, this.camera, this.renderer.domElement);

      this.health?.update(dt);
      if (this.spiders && this.character) {
        // Seznam cílů se přestavuje každý snímek, ale do stejného pole —
        // při šedesáti snímcích za sekundu by se jinak zbytečně alokovalo.
        this.spiderTargets.length = 0;
        this.spiderTargets.push({
          id: 'self',
          position: this.character.root.position,
          alive: this.health?.alive ?? true,
        });
        for (const [id, hp] of this.remoteHp) {
          const position = this.remotePlayers?.positionOf(id);
          if (position) this.spiderTargets.push({ id, position, alive: hp > 0 });
        }
        this.spiders.update(dt, this.spiderTargets, this.camera, this.renderer.domElement);
      }
    }

    this.camera.updateMatrixWorld();

    this.water.update(effectTime, this.camera, this.environment.sunDirection);
    this.splashes.update(effectTime, this.camera);
    this.clouds.update(effectTime, this.camera, this.environment.sunDirection);
    this.grass?.update(effectTime, this.camera, this.environment.sunDirection);
    this.environment.update(this.camera);
    this.terrain.update(this.camera);
    this.csm.update();

    const cpuMs = performance.now() - cpuStart;

    this.postFX.render(dt);
    this.updateAdaptiveResolution(dt);

    this.hud.update(dt, {
      renderer: this.renderer,
      terrain: this.terrain.stats,
      renderScale: this.renderScale,
      cpuMs,
      position: this.camera.position,
      walkMode: this.mode === 'playing',
      grassQuality: this.grass ? this.grassQuality : null,
    });
  };

  private updateAdaptiveResolution(dt: number): void {
    if (!ADAPTIVE.enabled) return;

    this.adaptiveFrames++;
    this.adaptiveElapsed += dt;
    this.adaptiveTimer += dt * 1000;
    if (this.adaptiveTimer < ADAPTIVE.interval) return;

    const fps = this.adaptiveFrames / Math.max(this.adaptiveElapsed, 1e-6);
    this.adaptiveTimer = 0;
    this.adaptiveFrames = 0;
    this.adaptiveElapsed = 0;

    const previousScale = this.renderScale;

    if (fps < ADAPTIVE.targetFps * 0.92) {
      if (this.grass && this.grassQuality > 0.35) {
        this.grassQuality = Math.max(0.35, this.grassQuality - 0.12);
        this.grass.setQuality(this.grassQuality);
      } else {
        this.renderScale = Math.max(ADAPTIVE.minScale, this.renderScale - ADAPTIVE.step);
      }
    } else if (fps >= ADAPTIVE.targetFps * 0.98) {
      if (this.renderScale < ADAPTIVE.maxScale) {
        this.renderScale = Math.min(ADAPTIVE.maxScale, this.renderScale + ADAPTIVE.step);
      } else if (this.grass && this.grassQuality < 1) {
        this.grassQuality = Math.min(1, this.grassQuality + 0.08);
        this.grass.setQuality(this.grassQuality);
      }
    }

    if (this.renderScale !== previousScale) this.applySize();
  }

  private applySize(): void {
    const width = Math.max(1, this.container.clientWidth || window.innerWidth || 1280);
    const height = Math.max(1, this.container.clientHeight || window.innerHeight || 720);

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.csm.updateFrustums();

    const scale = this.basePixelRatio * this.renderScale;
    const w = Math.max(2, Math.round(width * scale));
    const h = Math.max(2, Math.round(height * scale));

    this.renderer.setSize(w, h, false);
    this.postFX.setSize(w, h);
  }

  private onResize = (): void => {
    this.basePixelRatio = Math.min(window.devicePixelRatio || 1, RENDER.maxPixelRatio);
    this.applySize();
  };

  private onVisibilityChange = (): void => {
    if (document.hidden) this.stop();
    else this.start();
  };

  dispose(): void {
    this.stop();
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.characterMenu?.dispose();
    this.playerControls?.dispose();
    this.network?.disconnect();
    this.remotePlayers?.dispose();
    this.netStatusEl?.remove();
    this.fishing?.dispose();
    this.spiders?.dispose();
    this.weapon?.dispose();
    this.fishingPromptEl?.remove();
    this.healthBarEl?.remove();
    this.deathScreen?.dispose();
    this.chat?.dispose();
    this.fitPanel?.dispose();
    this.character?.dispose();
    this.characterAnimator?.dispose();
    this.postFX.dispose();
    this.terrain.dispose();
    this.water.dispose();
    this.clouds.dispose();
    this.grass?.dispose();
    this.splashes.dispose();
    this.environment.dispose();
    this.audio.dispose();
    this.csm.dispose();
    this.renderer.dispose();
  }
}

/** Převede konfiguraci postavy na tvar, který putuje po síti. */
function toNetAppearance(config: CharacterConfig): NetAppearance {
  return {
    gender: config.gender,
    hairstyle: config.hairstyle,
    beard: config.beard,
    eyebrows: config.eyebrows,
    skinTone: config.skinTone,
    hairColor: config.hairColor,
    outfit: config.outfit,
    hood: config.hood,
  };
}
